import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft, Edit2, FileText, Monitor, History, Settings, Upload, Download, Trash2, Plus, Calendar, Clock, MapPin, 
  User, Mail, Phone, Briefcase, BookOpen, FileBadge, FileIcon, Loader2, LogIn, LogOut, CheckCircle2, Shield
} from 'lucide-react';
import { collection, query, where, onSnapshot, orderBy, addDoc, serverTimestamp, deleteDoc, doc } from '../firebase';
import { db, auth, handleFirestoreError, OperationType, storage, ref, uploadBytes, getDownloadURL } from '../firebase';
import { notify } from '../services/notify';
import { cn } from '../../src/lib/utils';
import ConfirmModal from './ConfirmModal';
import SignatureCanvas from 'react-signature-canvas';

const getLocalAuthToken = () => localStorage.getItem('app_auth_token');
const isLocalSession = () => Boolean(getLocalAuthToken());

const localApiRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${getLocalAuthToken()}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const payload = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.error || `Request failed (${response.status})`);
  return payload as T;
};

const toLocalDate = (value: any): Date | null => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

interface EmployeeProfileViewProps {
  employee: any;
  orgId: string;
  currency: string;
  onBack: () => void;
  onEdit: () => void;
}

export default function EmployeeProfileView({ employee, orgId, currency, onBack, onEdit }: EmployeeProfileViewProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'logs' | 'assets' | 'documents'>('overview');
  
  const [logs, setLogs] = useState<any[]>([]);
  const [attendance, setAttendance] = useState<any[]>([]);
  const [assets, setAssets] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(true);
  const [isLoadingAssets, setIsLoadingAssets] = useState(true);
  const [isLoadingDocs, setIsLoadingDocs] = useState(true);

  // Attendance Filter State
  const [dateRange, setDateRange] = useState<{ start: string, end: string }>({
    start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [filterPreset, setFilterPreset] = useState<'30days' | '7days' | '3months' | 'custom'>('30days');

  const handleDatePreset = (preset: '30days' | '7days' | '3months') => {
    const end = new Date();
    const start = new Date();
    if (preset === '7days') start.setDate(end.getDate() - 7);
    else if (preset === '30days') start.setDate(end.getDate() - 30);
    else if (preset === '3months') start.setMonth(end.getMonth() - 3);
    
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
    setFilterPreset(preset);
  };

  const sigPad = useRef<SignatureCanvas>(null);

  // Asset Form State
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [newAsset, setNewAsset] = useState({ 
    name: '', 
    type: 'Laptop', 
    serialNumber: '', 
    givenDate: new Date().toISOString().split('T')[0],
    isSigned: false,
    signature: '',
    documentUrl: ''
  });

  // Document Form State
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [newDoc, setNewDoc] = useState({ title: '', category: 'Identification', fileUrl: '' });

  useEffect(() => {
    if (!employee?.id || !orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      setIsLoadingLogs(true);
      setIsLoadingAssets(true);
      setIsLoadingDocs(true);
      void Promise.all([
        localApiRequest<any[]>(`/api/organizations/${orgId}/employees/${employee.id}/logs`),
        localApiRequest<any[]>(`/api/organizations/${orgId}/attendances`),
        localApiRequest<any[]>(`/api/organizations/${orgId}/employees/${employee.id}/assets`),
        localApiRequest<any[]>(`/api/organizations/${orgId}/employees/${employee.id}/documents`),
      ])
        .then(([loadedLogs, loadedAttendance, loadedAssets, loadedDocuments]) => {
          if (cancelled) return;
          const normalizedAttendance = loadedAttendance
            .filter(record => record.employeeId === employee.id)
            .map(record => {
              const checkInTime = record.checkIn ?? record.check_in ?? null;
              const checkOutTime = record.checkOut ?? record.check_out ?? null;
              const checkIn = toLocalDate(checkInTime);
              const checkOut = toLocalDate(checkOutTime);
              return {
                ...record,
                checkInTime,
                checkOutTime,
                duration: record.duration ?? (checkIn && checkOut ? (checkOut.getTime() - checkIn.getTime()) / 60000 : null),
              };
            });
          setLogs(loadedLogs);
          setAttendance(normalizedAttendance);
          setAssets(loadedAssets);
          setDocuments(loadedDocuments);
        })
        .catch(error => {
          console.error('Local API error loading employee profile records:', error);
          if (!cancelled) notify.error(error instanceof Error ? error.message : 'Failed to load employee profile records');
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoadingLogs(false);
            setIsLoadingAssets(false);
            setIsLoadingDocs(false);
          }
        });
      return () => { cancelled = true; };
    }

    const logsQ = query(collection(db, 'employee_logs'), where('orgId', '==', orgId), where('employeeId', '==', employee.id), orderBy('timestamp', 'desc'));
    const unSubLogs = onSnapshot(logsQ, (snap) => {
      setLogs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => {
      handleFirestoreError(err, OperationType.LIST, 'employee_logs');
    });

    // Use email as a secondary check if employeeId doesn't match auth UID
    // In many apps, attendance is recorded by auth.uid, but employee record has different ID
    const attendanceQ = query(
      collection(db, 'attendance'), 
      where('orgId', '==', orgId), 
      where('employeeEmail', '==', employee.email), 
      orderBy('checkInTime', 'desc')
    );
    const unSubAttendance = onSnapshot(attendanceQ, (snap) => {
      setAttendance(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setIsLoadingLogs(false);
    }, (err) => {
      handleFirestoreError(err, OperationType.LIST, 'attendance');
      setIsLoadingLogs(false);
    });

    const assetsQ = query(collection(db, 'employee_assets'), where('orgId', '==', orgId), where('employeeId', '==', employee.id), orderBy('givenDate', 'desc'));
    const unSubAssets = onSnapshot(assetsQ, (snap) => {
      setAssets(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setIsLoadingAssets(false);
    }, (err) => {
      handleFirestoreError(err, OperationType.LIST, 'employee_assets');
      setIsLoadingAssets(false);
    });

    const docsQ = query(collection(db, 'employee_documents'), where('orgId', '==', orgId), where('employeeId', '==', employee.id), orderBy('uploadedAt', 'desc'));
    const unSubDocs = onSnapshot(docsQ, (snap) => {
      setDocuments(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setIsLoadingDocs(false);
    }, (err) => {
      handleFirestoreError(err, OperationType.LIST, 'employee_documents');
      setIsLoadingDocs(false);
    });

    return () => {
      unSubLogs();
      unSubAttendance();
      unSubAssets();
      unSubDocs();
    };
  }, [orgId, employee?.id, employee?.email]);

  const handleAddAsset = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let signatureBase64 = '';
      if (sigPad.current && !sigPad.current.isEmpty()) {
        signatureBase64 = sigPad.current.getTrimmedCanvas().toDataURL('image/png');
      }

      if (isLocalSession()) {
        const saved = await localApiRequest<any>(`/api/organizations/${orgId}/employees/${employee.id}/assets`, {
          method: 'POST',
          body: JSON.stringify({
            ...newAsset,
            signature: signatureBase64,
            isSigned: signatureBase64 !== ''
          })
        });
        setAssets(current => [saved, ...current]);
      } else {
        await addDoc(collection(db, 'employee_assets'), {
          ...newAsset,
          signature: signatureBase64,
          isSigned: signatureBase64 !== '',
          employeeId: employee.id,
          orgId,
          createdAt: serverTimestamp()
        });
      }
      setIsAssetModalOpen(false);
      setNewAsset({ 
        name: '', 
        type: 'Laptop', 
        serialNumber: '', 
        givenDate: new Date().toISOString().split('T')[0],
        isSigned: false,
        signature: '',
        documentUrl: ''
      });
      notify.confirm("Asset registered");
    } catch (err) {
      notify.error("Failed to add asset");
      console.error(err);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const toastId = notify.loading(`Uploading ${file.name}...`);
    try {
      const storagePath = `employee_assets/${employee.id}/${Date.now()}_${file.name}`;
      const localToken = typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
      let downloadUrl: string;

      if (localToken) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('targetPath', storagePath);

        const uploadResponse = await fetch('/api/files/upload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${localToken}` },
          body: formData,
        });
        const uploadResult = await uploadResponse.json();
        if (!uploadResponse.ok) throw new Error(uploadResult.error || 'Failed to upload file');

        const uploadedFile = uploadResult.file || uploadResult;
        downloadUrl = uploadedFile.url || uploadedFile.downloadUrl || uploadedFile.downloadURL;
        if (!downloadUrl) throw new Error('Upload did not return a file URL');
      } else {
        const fileRef = ref(storage, storagePath);
        const snapshot = await uploadBytes(fileRef, file);
        downloadUrl = await getDownloadURL(snapshot.ref);
      }
      
      setNewAsset(prev => ({ ...prev, documentUrl: downloadUrl }));
      notify.confirm("File uploaded", { id: toastId });
    } catch (err) {
      console.error("Upload failed:", err);
      notify.error("Upload failed", { id: toastId });
    }
  };

  const handleDocFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const toastId = notify.loading(`Uploading ${file.name}...`);
    try {
      const storagePath = `employee_docs/${employee.id}/${Date.now()}_${file.name}`;
      const localToken = typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
      let downloadUrl: string;

      if (localToken) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('targetPath', storagePath);

        const uploadResponse = await fetch('/api/files/upload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${localToken}` },
          body: formData,
        });
        const uploadResult = await uploadResponse.json();
        if (!uploadResponse.ok) throw new Error(uploadResult.error || 'Failed to upload file');

        const uploadedFile = uploadResult.file || uploadResult;
        downloadUrl = uploadedFile.url || uploadedFile.downloadUrl || uploadedFile.downloadURL;
        if (!downloadUrl) throw new Error('Upload did not return a file URL');
      } else {
        const fileRef = ref(storage, storagePath);
        const snapshot = await uploadBytes(fileRef, file);
        downloadUrl = await getDownloadURL(snapshot.ref);
      }
      
      setNewDoc(prev => ({ ...prev, fileUrl: downloadUrl }));
      notify.confirm("File uploaded", { id: toastId });
    } catch (err) {
      console.error("Upload failed:", err);
      notify.error("Upload failed", { id: toastId });
    }
  };

  const handleAddDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isLocalSession()) {
        const saved = await localApiRequest<any>(`/api/organizations/${orgId}/employees/${employee.id}/documents`, {
          method: 'POST',
          body: JSON.stringify(newDoc)
        });
        setDocuments(current => [saved, ...current]);
      } else {
        await addDoc(collection(db, 'employee_documents'), {
          ...newDoc,
          employeeId: employee.id,
          orgId,
          uploadedAt: serverTimestamp()
        });
      }
      setIsDocModalOpen(false);
      setNewDoc({ title: '', category: 'Identification', fileUrl: '' });
      notify.confirm("Document attached");
    } catch (err) {
      notify.error("Failed to add document");
      console.error(err);
    }
  };

  const handleDeleteAsset = async (id: string) => {
    try {
      if (isLocalSession()) {
        await localApiRequest(`/api/organizations/${orgId}/employees/${employee.id}/assets/${id}`, { method: 'DELETE' });
        setAssets(current => current.filter(asset => asset.id !== id));
      } else {
        await deleteDoc(doc(db, 'employee_assets', id));
      }
      notify.confirm("Asset deleted");
    } catch (err) {
      console.error('Failed to delete employee asset:', err);
      notify.error(err instanceof Error ? err.message : "Failed to delete asset");
    }
  };

  const handleDeleteDocument = async (id: string) => {
    try {
      if (isLocalSession()) {
        await localApiRequest(`/api/organizations/${orgId}/employees/${employee.id}/documents/${id}`, { method: 'DELETE' });
        setDocuments(current => current.filter(document => document.id !== id));
      } else {
        await deleteDoc(doc(db, 'employee_documents', id));
      }
      notify.confirm("Document deleted");
    } catch (err) {
      console.error('Failed to delete employee document:', err);
      notify.error(err instanceof Error ? err.message : "Failed to delete document");
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 border border-[#141414] rounded-full hover:bg-[#141414] hover:text-[#E4E3E0] transition-colors dark:border-white dark:hover:bg-white dark:hover:text-black">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{employee.firstName} {employee.lastName}</h1>
            <p className="text-sm opacity-50 italic serif">{employee.jobTitle} • {employee.department}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={onEdit} className="bg-white text-[#141414] border border-[#141414] px-4 py-2 rounded-full font-bold flex items-center gap-2 hover:bg-[#141414] hover:text-white transition-all text-sm dark:bg-black dark:text-white dark:border-white dark:hover:bg-white dark:hover:text-black">
            <Edit2 className="w-4 h-4" /> Edit Profile
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-[#141414]/10 dark:border-white/10 pb-2 overflow-x-auto custom-scrollbar">
        {[
          { id: 'overview', label: 'Overview', icon: User },
          { id: 'logs', label: 'Activity Logs', icon: History },
          { id: 'assets', label: 'Assets', icon: Monitor },
          { id: 'documents', label: 'Documents', icon: FileText }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all whitespace-nowrap",
              activeTab === tab.id 
                ? "bg-[#141414] text-[#E4E3E0] dark:bg-white dark:text-black" 
                : "hover:bg-[#141414]/5 dark:hover:bg-white/5 opacity-60 hover:opacity-100"
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white/50 dark:bg-white/5 border border-[#141414] dark:border-white/10 rounded-[2rem] p-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
               <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 dark:border-white/10 pb-2">Profile Details</h3>
               <div className="space-y-4">
                 <DetailItem icon={Mail} label="Email" value={employee.email} />
                 <DetailItem icon={Phone} label="Phone" value={employee.phone} />
                 <DetailItem icon={Briefcase} label="Department" value={employee.department} />
                 <DetailItem icon={Calendar} label="Joining Date" value={new Date(employee.joiningDate).toLocaleDateString()} />
               </div>
            </div>
            <div className="space-y-6">
               <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 dark:border-white/10 pb-2">Payroll Information</h3>
               <div className="space-y-4">
                 <DetailItem icon={Briefcase} label="Salary" value={`${currency} ${employee.salary?.toLocaleString() || 0} / ${employee.paymentFrequency}`} />
                 <DetailItem icon={Briefcase} label="Bank Name" value={employee.bankName || 'Not Set'} />
                 <DetailItem icon={Briefcase} label="Account" value={employee.accountNumber || 'Not Set'} />
               </div>
            </div>
            {employee.notes && (
              <div className="col-span-full space-y-4">
                 <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 dark:border-white/10 pb-2">Internal Notes</h3>
                 <p className="text-sm opacity-80 whitespace-pre-wrap">{employee.notes}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h3 className="text-lg font-bold italic serif tracking-tight">Attendance History</h3>
              <div className="flex flex-wrap items-center gap-2">
                <button 
                  onClick={() => handleDatePreset('7days')}
                  className={cn("px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all", filterPreset === '7days' ? "bg-[#141414] text-white dark:bg-white dark:text-black" : "bg-[#141414]/5 dark:bg-white/5 opacity-50")}
                >
                  Last 7 Days
                </button>
                <button 
                  onClick={() => handleDatePreset('30days')}
                  className={cn("px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all", filterPreset === '30days' ? "bg-[#141414] text-white dark:bg-white dark:text-black" : "bg-[#141414]/5 dark:bg-white/5 opacity-50")}
                >
                  Last 30 Days
                </button>
                <button 
                  onClick={() => handleDatePreset('3months')}
                  className={cn("px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all", filterPreset === '3months' ? "bg-[#141414] text-white dark:bg-white dark:text-black" : "bg-[#141414]/5 dark:bg-white/5 opacity-50")}
                >
                  3 Months
                </button>
                <div className="flex items-center gap-2 bg-[#141414]/5 dark:bg-white/5 px-3 py-1 rounded-full border border-[#141414]/10 dark:border-white/10">
                   <input 
                    type="date" 
                    value={dateRange.start} 
                    onChange={(e) => { setDateRange({...dateRange, start: e.target.value}); setFilterPreset('custom'); }}
                    className="bg-transparent text-[10px] uppercase tracking-widest font-bold outline-none"
                   />
                   <span className="opacity-30">•</span>
                   <input 
                    type="date" 
                    value={dateRange.end} 
                    onChange={(e) => { setDateRange({...dateRange, end: e.target.value}); setFilterPreset('custom'); }}
                    className="bg-transparent text-[10px] uppercase tracking-widest font-bold outline-none"
                   />
                </div>
              </div>
            </div>
            
            {isLoadingLogs ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>
            ) : (
              <div className="overflow-x-auto rounded-3xl border border-[#141414]/10 dark:border-white/10">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#141414]/5 dark:bg-white/5 border-b border-[#141414]/10 dark:border-white/10">
                      <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40 italic serif">Day</th>
                      <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40 italic serif">Date</th>
                      <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40 italic serif text-center">Check In</th>
                      <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40 italic serif text-center">Check Out</th>
                      <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40 italic serif text-right">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const days = [];
                      const end = new Date(dateRange.end);
                      const start = new Date(dateRange.start);
                      
                      // Normalize to midnight
                      end.setHours(23, 59, 59, 999);
                      start.setHours(0, 0, 0, 0);

                      const diffTime = Math.abs(end.getTime() - start.getTime());
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      
                      // Generate days in range
                      for (let i = 0; i < diffDays; i++) {
                        const date = new Date(end);
                        date.setDate(end.getDate() - i);
                        date.setHours(0, 0, 0, 0);
                        
                        if (date < start) break;

                        const dayAttendance = attendance.filter(a => {
                          if (!a.checkInTime) return false;
                          const aDate = toLocalDate(a.checkInTime);
                          if (!aDate) return false;
                          aDate.setHours(0, 0, 0, 0);
                          return aDate.getTime() === date.getTime();
                        });

                        if (dayAttendance.length === 0) {
                          days.push({ date: new Date(date), logs: [null] });
                        } else {
                          days.push({ date: new Date(date), logs: [...dayAttendance].sort((a, b) => (toLocalDate(a.checkInTime)?.getTime() || 0) - (toLocalDate(b.checkInTime)?.getTime() || 0)) });
                        }
                      }

                      return days.map(({ date, logs }, dayIdx) => (
                        <React.Fragment key={date.toISOString()}>
                          {logs.map((log, logIdx) => {
                            const isFirst = logIdx === 0;
                            return (
                              <tr 
                                key={log?.id || `empty-${date.toISOString()}`} 
                                className={cn(
                                  "border-b border-[#141414]/5 dark:border-white/5 hover:bg-[#141414]/5 dark:hover:bg-white/5 transition-colors",
                                  !log && "opacity-30"
                                )}
                              >
                                <td className="p-4 text-xs font-bold serif italic">
                                  {isFirst ? date.toLocaleDateString('en-US', { weekday: 'long' }) : ''}
                                </td>
                                <td className="p-4 text-xs font-mono">
                                  {isFirst ? date.toLocaleDateString() : ''}
                                </td>
                                <td className="p-4 text-xs text-center font-bold">
                                  {toLocalDate(log?.checkInTime)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || '—'}
                                </td>
                                <td className="p-4 text-xs text-center font-bold">
                                  {toLocalDate(log?.checkOutTime)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || '—'}
                                </td>
                                <td className="p-4 text-xs text-right font-mono font-bold">
                                  {log?.duration ? `${Math.round(log.duration)}m` : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            )}

            {/* Other non-attendance logs */}
            {logs.length > 0 && (
              <div className="space-y-4 pt-6 mt-6 border-t border-[#141414]/10 dark:border-white/10">
                <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">System Activity</h3>
                <div className="space-y-4">
                  {logs.map((log: any) => (
                    <div key={log.id} className="flex items-start gap-4 p-4 rounded-xl border border-[#141414]/10 dark:border-white/10 bg-[#141414]/5 dark:bg-white/5">
                      <div className="p-2 bg-white dark:bg-black rounded-lg border border-[#141414]/10 dark:border-white/10 text-primary">
                        {log.type === 'login' ? <Monitor className="w-4 h-4" /> : <History className="w-4 h-4" />}
                      </div>
                      <div>
                        <p className="font-bold text-sm">{log.action}</p>
                        <p className="text-xs opacity-60 mt-1">{toLocalDate(log.timestamp)?.toLocaleString() || 'Just now'} • {log.details || log.type}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'assets' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold italic serif tracking-tight">Assigned Assets</h3>
              <button onClick={() => setIsAssetModalOpen(true)} className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] dark:bg-white dark:text-black px-4 py-2 rounded-full font-bold hover:opacity-90 transition-opacity text-sm">
                <Plus className="w-4 h-4" /> Register Asset
              </button>
            </div>
            {isLoadingAssets ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>
            ) : assets.length === 0 ? (
              <div className="text-center py-12 opacity-50 italic serif">No assets assigned yet.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {assets.map((asset: any) => (
                  <div key={asset.id} className="p-4 rounded-2xl border border-[#141414]/10 dark:border-white/10 bg-[#141414]/5 dark:bg-white/5 relative group">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <Monitor className="w-8 h-8 opacity-50" />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold">{asset.name}</p>
                            {asset.isSigned && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                          </div>
                          <p className="text-xs opacity-50">{asset.type} • S/N: {asset.serialNumber}</p>
                          <p className="text-[10px] uppercase tracking-widest font-bold opacity-40 mt-2">Given: {new Date(asset.givenDate).toLocaleDateString()}</p>
                          
                          <div className="mt-3 flex flex-wrap gap-2">
                            {asset.signature && (
                               <div className="p-1 bg-white rounded border border-[#141414]/10">
                                 <img src={asset.signature} alt="Signature" className="h-8 grayscale" />
                               </div>
                            )}
                            {asset.documentUrl && (
                               <div className="flex items-center gap-1 text-[10px] bg-blue-500/10 text-blue-500 px-2 py-1 rounded-full font-bold">
                                 <FileText className="w-3 h-3" /> Document
                               </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <button onClick={() => handleDeleteAsset(asset.id)} className="p-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:bg-red-500/10 rounded-full">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold italic serif tracking-tight">Employee Documents</h3>
              <button onClick={() => setIsDocModalOpen(true)} className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] dark:bg-white dark:text-black px-4 py-2 rounded-full font-bold hover:opacity-90 transition-opacity text-sm">
                <Plus className="w-4 h-4" /> Add Document
              </button>
            </div>
            {isLoadingDocs ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12 opacity-50 italic serif">No documents attached yet.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {documents.map((docItem: any) => (
                  <div key={docItem.id} className="p-4 rounded-2xl border border-[#141414]/10 dark:border-white/10 bg-[#141414]/5 dark:bg-white/5 flex items-start justify-between group">
                    <div className="flex items-start gap-3">
                      <div className="p-3 bg-blue-500/10 text-blue-500 rounded-xl">
                        <FileText className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-bold">{docItem.title}</p>
                        <p className="text-xs opacity-50">{docItem.category}</p>
                        <p className="text-[10px] uppercase tracking-widest font-bold opacity-40 mt-1">Uploaded: {toLocalDate(docItem.uploadedAt)?.toLocaleDateString() || 'Just now'}</p>
                        {docItem.fileUrl && (
                          <a href={docItem.fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline mt-2 inline-block">
                            View Document
                          </a>
                        )}
                      </div>
                    </div>
                    <button onClick={() => handleDeleteDocument(docItem.id)} className="p-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:bg-red-500/10 rounded-full">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {isAssetModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-[#141414]/40 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsAssetModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] dark:bg-[#000000] w-full max-w-lg rounded-[2rem] overflow-hidden shadow-2xl border border-[#141414] dark:border-white/10 flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-[#141414]/10 dark:border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold italic serif tracking-tight">Register Asset</h2>
              <button onClick={() => setIsAssetModalOpen(false)} className="p-2 hover:bg-[#141414]/10 dark:hover:bg-white/10 rounded-full transition-colors"><span className="text-xl">&times;</span></button>
            </div>
            <form onSubmit={handleAddAsset} className="p-6 space-y-6 overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Asset Name</label>
                  <input required type="text" className="w-full bg-white/50 dark:bg-white/5 border border-[#141414] dark:border-white/10 p-3 rounded-xl outline-none" value={newAsset.name} onChange={e => setNewAsset({...newAsset, name: e.target.value})} placeholder="e.g. MacBook Pro M2" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Asset Type</label>
                  <select className="w-full bg-white/50 dark:bg-white/5 border border-[#141414] dark:border-white/10 p-3 rounded-xl outline-none" value={newAsset.type} onChange={e => setNewAsset({...newAsset, type: e.target.value})}>
                    <option>Laptop</option>
                    <option>Phone</option>
                    <option>Vehicle</option>
                    <option>Keys</option>
                    <option>Hardware/Equipment</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Serial Number</label>
                  <input type="text" className="w-full bg-white/50 dark:bg-white/5 border border-[#141414] dark:border-white/10 p-3 rounded-xl outline-none" value={newAsset.serialNumber} onChange={e => setNewAsset({...newAsset, serialNumber: e.target.value})} placeholder="SN-123456" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Given Date</label>
                  <input required type="date" className="w-full bg-white/50 dark:bg-white/5 border border-[#141414] dark:border-white/10 p-3 rounded-xl outline-none" value={newAsset.givenDate} onChange={e => setNewAsset({...newAsset, givenDate: e.target.value})} />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Upload Document / Proof</label>
                <div className="flex items-center gap-4">
                  <label className="flex-1 cursor-pointer">
                    <input type="file" className="hidden" onChange={handleFileUpload} />
                    <div className="flex items-center gap-3 bg-white/50 dark:bg-white/5 border border-[#141414] dark:border-white/10 p-3 rounded-xl hover:bg-[#141414]/5 dark:hover:bg-white/5 transition-colors">
                      <Upload className="w-4 h-4 opacity-50" />
                      <span className="text-sm opacity-60">{newAsset.documentUrl || 'Choose file...'}</span>
                    </div>
                  </label>
                  {newAsset.documentUrl && (
                    <button type="button" onClick={() => setNewAsset({...newAsset, documentUrl: ''})} className="p-3 text-red-500 hover:bg-red-500/10 rounded-xl transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Employee Signature</label>
                  <button type="button" onClick={() => sigPad.current?.clear()} className="text-[10px] uppercase tracking-widest font-bold text-red-500 hover:underline">Clear</button>
                </div>
                <div className="border border-[#141414] dark:border-white/10 rounded-xl bg-white overflow-hidden">
                  <SignatureCanvas 
                    ref={sigPad}
                    penColor='#141414'
                    canvasProps={{className: 'sigCanvas w-full h-32 cursor-crosshair'}}
                  />
                </div>
              </div>

              <button type="submit" className="w-full bg-[#141414] text-[#E4E3E0] dark:bg-white dark:text-black py-4 rounded-xl font-bold hover:opacity-90 transition-opacity sticky bottom-0">Save Asset & Signature</button>
            </form>
          </div>
        </div>
      )}

      {isDocModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-[#141414]/40 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsDocModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] dark:bg-[#000000] w-full max-w-md rounded-[2rem] overflow-hidden shadow-2xl border border-[#141414] dark:border-white/10 flex flex-col">
            <div className="p-6 border-b border-[#141414]/10 dark:border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold italic serif tracking-tight">Add Document</h2>
              <button onClick={() => setIsDocModalOpen(false)} className="p-2 hover:bg-[#141414]/10 dark:hover:bg-white/10 rounded-full transition-colors"><span className="text-xl">&times;</span></button>
            </div>
            <form onSubmit={handleAddDocument} className="p-6 space-y-6 max-h-[calc(100dvh-8rem)] overflow-y-auto overscroll-contain">
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Document Title</label>
                <input required type="text" className="w-full bg-white/50 dark:bg-white/5 border border-[#141414] dark:border-white/10 p-3 rounded-xl outline-none" value={newDoc.title} onChange={e => setNewDoc({...newDoc, title: e.target.value})} placeholder="e.g. Passport Copy" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Category</label>
                <select className="w-full bg-white/50 dark:bg-white/5 border border-[#141414] dark:border-white/10 p-3 rounded-xl outline-none" value={newDoc.category} onChange={e => setNewDoc({...newDoc, category: e.target.value})}>
                  <option>Identification (ID, Passport)</option>
                  <option>Education (Degree, Certificate)</option>
                  <option>Military / Army</option>
                  <option>Contract / Agreement</option>
                  <option>Medical</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Upload Document / Link</label>
                <div className="grid grid-cols-1 gap-3">
                   <label className="cursor-pointer">
                      <input type="file" className="hidden" onChange={handleDocFileUpload} />
                      <div className="flex items-center gap-3 bg-white/50 dark:bg-white/5 border border-[#141414] dark:border-white/10 p-3 rounded-xl hover:bg-[#141414]/5 dark:hover:bg-white/5 transition-colors">
                        <Upload className="w-4 h-4 opacity-50" />
                        <span className="text-sm opacity-60">Upload from device...</span>
                      </div>
                   </label>
                   <div className="flex items-center gap-2">
                     <div className="h-px bg-[#141414]/10 flex-1" />
                     <span className="text-[8px] uppercase font-bold opacity-30">or paste link</span>
                     <div className="h-px bg-[#141414]/10 flex-1" />
                   </div>
                   <input type="url" className="w-full bg-white/50 dark:bg-white/5 border border-[#141414] dark:border-white/10 p-3 rounded-xl outline-none" value={newDoc.fileUrl} onChange={e => setNewDoc({...newDoc, fileUrl: e.target.value})} placeholder="https://drive.google.com/..." />
                </div>
              </div>
              <button type="submit" className="w-full bg-[#141414] text-[#E4E3E0] dark:bg-white dark:text-black py-4 rounded-xl font-bold hover:opacity-90 transition-opacity">Save Document</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailItem({ icon: Icon, label, value }: { icon: any, label: string, value: string }) {
  return (
    <div className="flex items-center gap-4">
      <div className="p-3 bg-[#141414]/5 dark:bg-white/5 rounded-xl">
        <Icon className="w-5 h-5 opacity-70" />
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest font-bold opacity-40 mb-1">{label}</p>
        <p className="font-medium">{value}</p>
      </div>
    </div>
  );
}
